// 2026-11-02 (Keaton: picking a reference picture crashed the home-screen app on his iPhone): open a file picker with
// the page as light as it can be. iOS runs the photo picker alongside the app and drops a web app that holds too
// much at that moment, so every playing video (a looping scene clip holds a decoder and its frames) is paused while
// the picker is up and resumed after; `onQuiet` lets a screen drop its own heavy parts (a grid of pictures) too.

/** Pause every playing video; returns a function that resumes the ones it paused. */
export function quietVideos(doc: Pick<Document, "querySelectorAll"> = document): () => void {
  const paused: HTMLVideoElement[] = [];
  doc.querySelectorAll("video").forEach((v) => {
    if (!v.paused) {
      v.pause();
      paused.push(v);
    }
  });
  let done = false;
  return () => {
    if (done) return;
    done = true;
    for (const v of paused) if (v.isConnected) void v.play()?.catch(() => undefined);
  };
}

/** Opens `input`'s picker (call inside the tap). The page is quiet until a file is chosen or the picker closes. */
export function openPicker(input: HTMLInputElement | null | undefined, onQuiet?: (quiet: boolean) => void): void {
  if (!input) return;
  const resume = quietVideos();
  onQuiet?.(true);
  const openedAt = Date.now();
  let ended = false;
  const end = () => {
    if (ended) return;
    ended = true;
    input.removeEventListener("change", end);
    input.removeEventListener("cancel", end);
    window.removeEventListener("focus", onFocus);
    document.removeEventListener("visibilitychange", onVisible);
    resume();
    onQuiet?.(false);
  };
  // back from a picker closed without a file (older Safari has no cancel event): focus or visible again, a moment on
  const later = () => window.setTimeout(end, 600);
  const onFocus = () => Date.now() - openedAt > 800 && later();
  const onVisible = () => document.visibilityState === "visible" && Date.now() - openedAt > 800 && later();
  input.addEventListener("change", end);
  input.addEventListener("cancel", end);
  window.addEventListener("focus", onFocus);
  document.addEventListener("visibilitychange", onVisible);
  input.click();
}
